import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { endOfDay, startOfDay } from "date-fns";
import { Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { cn } from "@/lib/utils";
import { ExportFilterPopover } from "./components/ExportFilterPopover";
import { RevenueLogsPanel } from "./components/RevenueLogsPanel";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import { mapPartnerSummaryMoney } from "./utils/partnerSummaryMoney";
import { PartnerRevenueLogRecord } from "@/types/partner-dashboard";
import { LocalStorageKeys } from "../login/hooks/useLocalStorage";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  GuesteriaPartnerRevenueRow,
  mapOverviewToRow,
} from "./guesteriaPartnerRevenueModel";
import {
  PartnerOrdersTable,
  decodePartnerRoomFilter,
} from "@/components/pages/guesteria-partners/components/PartnerOrdersTable";
import { useOrdersRoomFilter } from "@/components/pages/guesteria-partners/hooks/useOrdersRoomFilter";
import {
  useHotelPartnerOrders,
  useHotelPartnerOrdersSummary,
} from "./hooks/partner-orders.hooks";
import { PartnerOrdersCatalogFilter } from "@/types/partner-order";
import { PartnerPeriodSelector } from "@/components/pages/guesteria-partners/components/PartnerPeriodSelector";
import { PartnerAttributionSection } from "@/components/pages/guesteria-partners/components/PartnerAttributionSection";
import { usePartnerRevenuePeriod } from "@/components/pages/guesteria-partners/hooks/usePartnerRevenuePeriod";
import { usePartnerAttribution } from "@/components/pages/guesteria-partners/hooks/usePartnerAttribution";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";

const ORDERS_PAGE_SIZE = 10;

type LocationState = {
  row?: GuesteriaPartnerRevenueRow;
};

// Sim, 2026-09-29: this page hard-coded "1st of month to today" with no
// picker at all — orders are filtered by service date now (task 0033), so
// every future ("Expected") booking was invisible and there was no way to
// change the range. Default: 1st of this month through the end of next
// month, same as the sibling list/summary pages, now with a real picker.
function toIsoDateRange(range: PartnerRevenueDateRange): {
  from: string;
  to: string;
} {
  return {
    from: startOfDay(range.from).toISOString(),
    to: endOfDay(range.to).toISOString(),
  };
}

// Null-safe: a missing/malformed value must render as "—", never throw
// (sim hit a white screen from `undefined.toLocaleString()`, 2026-09-27).
function formatMoneyValue(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  return `€${value.toLocaleString("en-GB")}`;
}

export const GuesteriaPartnersRevenueDetailPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { capabilities } = useAccessCapabilities();
  const canExport = capabilities.canManageBilling;
  // GST sim fix, 2026-09-30: `GET /files/:id` needs `x-tenant-id` to resolve
  // a file's region — same fix as `UsersTable.tsx` (commit e591d62e). Without
  // it the hotel logo request 403s.
  const { userProfile } = useAuthenticatedUser();
  const tenantId = userProfile?.tenantId;
  const { hotelId } = useParams<{ hotelId: string }>();
  const { state } = useLocation() as { state: LocationState | null };
  const rowFromNavigation =
    state?.row && hotelId && state.row.id === hotelId ? state.row : undefined;

  const [row, setRow] = React.useState<GuesteriaPartnerRevenueRow | undefined>(
    rowFromNavigation,
  );
  const [rowLoading, setRowLoading] = React.useState(!rowFromNavigation);

  const [selectedExportFormat, setSelectedExportFormat] = React.useState<
    "CSV" | "Excel" | null
  >("CSV");
  const [logs, setLogs] = React.useState<PartnerRevenueLogRecord[]>([]);
  const [logsLoading, setLogsLoading] = React.useState(false);
  const [ordersCatalog, setOrdersCatalog] =
    React.useState<PartnerOrdersCatalogFilter>("all");
  const [ordersPage, setOrdersPage] = React.useState(1);
  const [roomFilter, setRoomFilter] = useOrdersRoomFilter();
  const { attribution: ordersAttribution, roomId: ordersRoomId } =
    decodePartnerRoomFilter(roomFilter);
  const { range: dateRange } = usePartnerRevenuePeriod();
  const { from: ordersFrom, to: ordersTo } = toIsoDateRange(dateRange);
  const { data: hotelOrdersSummary } = useHotelPartnerOrdersSummary(
    hotelId,
    ordersFrom,
    ordersTo,
  );
  const {
    data: hotelOrdersResponse,
    isLoading: isHotelOrdersLoading,
    isError: isHotelOrdersError,
  } = useHotelPartnerOrders(
    hotelId,
    ordersFrom,
    ordersTo,
    ordersCatalog,
    ordersPage,
    ORDERS_PAGE_SIZE,
    ordersAttribution,
    ordersRoomId,
  );

  // Task 0041 — "Activities by room", same shared range as the rest of the page.
  const attribution = usePartnerAttribution(
    hotelId,
    dateRange,
    Boolean(hotelId),
  );

  React.useEffect(() => {
    if (rowFromNavigation) {
      setRow(rowFromNavigation);
      setRowLoading(false);
      return;
    }

    if (!hotelId) {
      setRow(undefined);
      setRowLoading(false);
      return;
    }

    const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
    if (!tenantId) {
      setRow(undefined);
      setRowLoading(false);
      return;
    }

    let cancelled = false;
    setRow(undefined);
    setRowLoading(true);

    const loadRow = async () => {
      try {
        const data = await partnerDashboardService.getHotels(tenantId, {
          from: ordersFrom,
          to: ordersTo,
          hotelIds: [hotelId],
        });
        if (!cancelled) {
          const overview = data[0];
          setRow(overview ? mapOverviewToRow(overview) : undefined);
        }
      } catch {
        if (!cancelled) {
          setRow(undefined);
        }
      } finally {
        if (!cancelled) {
          setRowLoading(false);
        }
      }
    };

    void loadRow();
    return () => {
      cancelled = true;
    };
  }, [hotelId, rowFromNavigation, ordersFrom, ordersTo]);

  React.useEffect(() => {
    const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
    if (!tenantId || !hotelId) {
      setLogs([]);
      return;
    }

    let isMounted = true;
    const loadRevenueLogs = async () => {
      try {
        setLogsLoading(true);
        const data = await partnerDashboardService.getRevenueLogs(tenantId, {
          from: ordersFrom,
          to: ordersTo,
          hotelId,
          page: 1,
          limit: 20,
        });
        if (isMounted) {
          setLogs(data.data);
        }
      } catch {
        if (isMounted) {
          setLogs([]);
        }
      } finally {
        if (isMounted) {
          setLogsLoading(false);
        }
      }
    };

    void loadRevenueLogs();
    return () => {
      isMounted = false;
    };
  }, [hotelId, ordersFrom, ordersTo]);

  const handleExport = React.useCallback(
    async (nextFormat: "CSV" | "Excel" | null) => {
      if (!canExport) return;

      setSelectedExportFormat(nextFormat);
      if (!nextFormat || !hotelId) return;

      const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
      if (!tenantId) return;

      const format = nextFormat === "CSV" ? "csv" : "xlsx";
      const blob = await partnerDashboardService.exportRevenueLogs(tenantId, {
        from: ordersFrom,
        to: ordersTo,
        hotelId,
        format,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `partner-revenue-logs-${hotelId}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    [canExport, hotelId, ordersFrom, ordersTo],
  );

  if (rowLoading) {
    return (
      <div className="p-6">
        <Button
          type="button"
          variant="ghost"
          className="mb-6 h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <Loader2 className="size-8 animate-spin text-zinc-400" aria-hidden />
          <p className="text-sm text-zinc-500">
            {t("billing.guesteriaPartnersRevenue.details.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="p-6">
        <Button
          type="button"
          variant="ghost"
          className="mb-6 h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        <p className="text-sm text-zinc-600">
          {t("billing.guesteriaPartnersRevenue.details.notFound")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6">
      <div className="mb-6 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          className="h-8 rounded-md bg-transparent px-0 py-1 text-base font-normal text-black hover:bg-transparent"
          onClick={() => navigate("/guesteria-partners-revenue")}
        >
          <Undo2 className="size-5" />
          {t("billing.guesteriaPartnersRevenue.details.back")}
        </Button>
        <PartnerPeriodSelector />
        {canExport ? (
          <ExportFilterPopover
            selectedFormat={selectedExportFormat}
            onApply={(nextFormat) => {
              void handleExport(nextFormat);
            }}
          />
        ) : null}
      </div>

      <div className="mx-auto mb-6 flex max-w-xl flex-col items-center gap-3 py-2">
        <PresignedImageWithPlaceholder
          className="h-32 w-32 rounded-2xl object-cover shadow-sm"
          photo={row.hotelLogoId}
          tenantId={tenantId}
          alt={row.hotelBrand}
          placeholder={
            <div
              className={cn(
                "flex h-32 w-32 items-center justify-center rounded-2xl font-serif text-5xl font-semibold text-amber-300 shadow-sm",
                row.hotelLogoClassName,
              )}
            >
              {row.hotelLogoLetter}
            </div>
          }
        />
        <h2 className="text-center text-lg leading-none font-medium text-zinc-950">
          {row.hotelBrand}
        </h2>
      </div>

      <div className="mb-4 rounded-xl bg-white px-6 pt-6 pb-0">
        <div className="grid grid-cols-[140px_1fr] gap-x-6 gap-y-4">
          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.admin")}:
          </span>
          <span className="flex min-h-8 items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-200 text-sm font-medium text-zinc-700">
              {row.adminInitials}
            </span>
            <span className="text-sm leading-normal font-medium text-zinc-950">
              {row.adminName}
            </span>
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.partner")}:
          </span>
          <span className="flex min-h-8 w-fit flex-col items-center gap-1">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-600 font-serif text-xl font-semibold text-white">
              G
            </span>
            <span className="inline-flex h-[12px] w-[40px] items-center justify-center text-center text-[10px] leading-[100%] font-normal text-[#0A0A0A]">
              {row.partnerName}
            </span>
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.details.currency")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {t("billing.guesteriaPartnersRevenue.details.currencyEuro")}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.revenue")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {hotelOrdersSummary
              ? formatMoneyValue(
                  mapPartnerSummaryMoney(hotelOrdersSummary).revenue,
                )
              : `€${row.revenueEur.toLocaleString("en-GB")}`}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.pms")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {row.pms}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.billingPlan")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {row.billingPlan}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("billing.guesteriaPartnersRevenue.details.outOfCatalog")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {hotelOrdersSummary
              ? t("billing.guesteriaPartnersRevenue.outOfCatalog.value", {
                  count: hotelOrdersSummary.outOfCatalog.count,
                  revenue: formatMoneyValue(
                    mapPartnerSummaryMoney(hotelOrdersSummary).bookedOutside
                      .booked,
                  ),
                })
              : "-"}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("partners.detail.earned")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {hotelOrdersSummary
              ? formatMoneyValue(hotelOrdersSummary.earnings)
              : "-"}
          </span>

          <span className="flex min-h-8 items-center text-sm leading-normal font-medium text-zinc-950">
            {t("partners.detail.expected")}:
          </span>
          <span className="flex min-h-8 items-center text-sm leading-normal text-zinc-500">
            {hotelOrdersSummary
              ? formatMoneyValue(hotelOrdersSummary.pendingEarnings)
              : "-"}
          </span>
        </div>
      </div>

      <div className="mb-4">
        <PartnerAttributionSection
          report={attribution.data}
          isLoading={attribution.isLoading}
          isError={attribution.isError}
        />
      </div>

      <RevenueLogsPanel logs={logs} loading={logsLoading} />

      <div className="rounded-xl bg-white p-6">
        <h3 className="mb-4 text-base font-semibold">
          {t("billing.guesteriaPartnersRevenue.details.ordersTitle")}
        </h3>
        <PartnerOrdersTable
          orders={hotelOrdersResponse?.data ?? []}
          total={hotelOrdersResponse?.total ?? 0}
          loading={isHotelOrdersLoading}
          error={isHotelOrdersError}
          page={ordersPage}
          limit={ORDERS_PAGE_SIZE}
          onPageChange={setOrdersPage}
          catalog={ordersCatalog}
          onCatalogChange={setOrdersCatalog}
          showCommission={false}
          hotelId={hotelId}
          roomFilter={roomFilter}
          onRoomFilterChange={setRoomFilter}
        />
      </div>
    </div>
  );
};
